import React from 'react';
import { Box, Typography, Avatar, Link, Chip } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { colors } from '../../../theme/colors';

// Feed item types
export type FeedItemType =
  | 'description'
  | 'user_message'
  | 'assistant_message'
  | 'system_message'
  | 'artifact_request'
  | 'artifact_output'
  | 'artifact_decision'
  | 'artifact_upload'
  | 'agent_action'
  | 'participant_message'
  | 'assessment';

// Actor types for the feed
export type FeedActor = 'user' | 'planapus' | 'system' | 'agent' | 'participant';

// Attachment type
export interface FeedAttachment {
  id: string;
  name: string;
  url?: string;
  type?: string;
}

// Feed item structure
export interface FeedItem {
  id: string;
  type: FeedItemType;
  actor: FeedActor;
  timestamp: string;
  content?: string;
  attachments?: FeedAttachment[];
  meta?: Record<string, unknown>;
}

interface StepInteractionFeedProps {
  items: FeedItem[];
  stepTitle: string;
  /**
   * When true, use compact styling with reduced spacing.
   * Used for completed steps to minimize vertical height.
   */
  compact?: boolean;
}

// Get actor display info
const getActorInfo = (actor: FeedActor) => {
  switch (actor) {
    case 'planapus':
      return {
        label: 'Planapus',
        icon: <SmartToyIcon sx={{ fontSize: 16 }} />,
        bgColor: colors.blue[600],
        bubbleBg: colors.blue[50],
        bubbleBorder: colors.blue[200],
      };
    case 'user':
      return {
        label: 'You',
        icon: <PersonIcon sx={{ fontSize: 16 }} />,
        bgColor: colors.gray[600],
        bubbleBg: colors.gray[100],
        bubbleBorder: colors.gray[300],
      };
    case 'system':
      return {
        label: 'System',
        icon: <SettingsIcon sx={{ fontSize: 16 }} />,
        bgColor: colors.gray[400],
        bubbleBg: colors.amber[50],
        bubbleBorder: colors.amber[100],
      };
    case 'agent':
      return {
        label: 'Task Agent',
        icon: <AssignmentIcon sx={{ fontSize: 16 }} />,
        bgColor: colors.purple[600],
        bubbleBg: colors.purple[50],
        bubbleBorder: colors.purple[100],
      };
    case 'participant':
      return {
        label: 'Participant',
        icon: <GroupIcon sx={{ fontSize: 16 }} />,
        bgColor: colors.green[600],
        bubbleBg: colors.green[50],
        bubbleBorder: colors.green[100],
      };
    default:
      return {
        label: 'Unknown',
        icon: <PersonIcon sx={{ fontSize: 16 }} />,
        bgColor: colors.gray[500],
        bubbleBg: colors.gray[100],
        bubbleBorder: colors.gray[300],
      };
  }
};

// Format timestamp
const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Get type-specific icon for artifacts
const getArtifactIcon = (type: FeedItemType) => {
  switch (type) {
    case 'description':
      return <DescriptionIcon sx={{ fontSize: 14 }} />;
    case 'artifact_request':
      return <AssignmentIcon sx={{ fontSize: 14 }} />;
    case 'artifact_upload':
      return <UploadFileIcon sx={{ fontSize: 14 }} />;
    case 'artifact_decision':
      return <CheckCircleIcon sx={{ fontSize: 14 }} />;
    case 'assessment':
      return <AssessmentIcon sx={{ fontSize: 14 }} />;
    default:
      return null;
  }
};

// Individual feed item component
const FeedItemBubble: React.FC<{ item: FeedItem }> = ({ item }) => {
  const actorInfo = getActorInfo(item.actor);
  const isDescription = item.type === 'description';
  const isArtifact = item.type.startsWith('artifact_');
  const isAssessment = item.type === 'assessment';
  const artifactIcon = getArtifactIcon(item.type);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
      {/* Avatar */}
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: actorInfo.bgColor,
          flexShrink: 0,
        }}
      >
        {actorInfo.icon}
      </Avatar>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header: Actor + Timestamp */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '0.8rem' }}
          >
            {actorInfo.label}
          </Typography>
          {isDescription && (
            <Chip
              label="Step Info"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: colors.blue[100],
                color: colors.blue[700],
              }}
            />
          )}
          {isAssessment && (
            <Chip
              icon={<AssessmentIcon sx={{ fontSize: 12 }} />}
              label="Progress Check"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: colors.green[50],
                color: colors.green[600],
                '& .MuiChip-icon': { color: colors.green[600] },
              }}
            />
          )}
          {isArtifact && artifactIcon && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: colors.gray[500] }}>
              {artifactIcon}
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}
          >
            {formatTimestamp(item.timestamp)}
          </Typography>
        </Box>

        {/* Message Bubble */}
        <Box
          sx={{
            bgcolor: actorInfo.bubbleBg,
            border: `1px solid ${actorInfo.bubbleBorder}`,
            borderRadius: 2,
            px: 2,
            py: 1.5,
            ...(isDescription && {
              borderLeft: `3px solid ${colors.blue[500]}`,
            }),
            ...(isArtifact && {
              borderLeft: `3px solid ${colors.purple[500]}`,
            }),
            ...(isAssessment && {
              borderLeft: `3px solid ${colors.green[600]}`,
            }),
          }}
        >
          {item.content && (
            <Box
              sx={{
                color: colors.textPrimary,
                lineHeight: 1.6,
                fontSize: '0.875rem',
                '& p': { margin: 0, mb: 1, '&:last-child': { mb: 0 } },
                '& ul, & ol': { mt: 0.5, mb: 1, pl: 2.5 },
                '& li': { mb: 0.5 },
                '& code': {
                  bgcolor: colors.gray[100],
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                },
                '& pre': {
                  bgcolor: colors.gray[100],
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': { bgcolor: 'transparent', p: 0 },
                },
                '& strong': { fontWeight: 600 },
                '& a': { color: colors.blue[600] },
              }}
            >
              <ReactMarkdown>{item.content}</ReactMarkdown>
            </Box>
          )}

          {/* Artifact-specific metadata */}
          {isArtifact && item.meta && renderArtifactMeta(item)}

          {/* Attachments */}
          {item.attachments && item.attachments.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {item.attachments.map((att) => (
                <Chip
                  key={att.id}
                  icon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                  label={att.name}
                  size="small"
                  component={att.url ? 'a' : 'span'}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable={!!att.url}
                  sx={{
                    bgcolor: 'white',
                    border: `1px solid ${colors.gray[300]}`,
                    '& .MuiChip-label': { fontSize: '0.75rem' },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Render artifact-specific metadata
const renderArtifactMeta = (item: FeedItem): React.ReactNode => {
  if (!item.meta) return null;

  const meta = item.meta as Record<string, unknown>;
  const artifactType = meta.type ? String(meta.type) : null;
  const status = meta.status ? String(meta.status) : null;
  const publicUrl = meta.publicUrl ? String(meta.publicUrl) : null;

  const hasContent = artifactType || status || publicUrl;
  if (!hasContent) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      {artifactType !== null && (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>Type:</strong> {artifactType}
        </Typography>
      )}
      {status !== null && (
        <Chip
          label={status}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 500,
            bgcolor: status === 'COMPLETED' ? colors.green[50] : colors.amber[50],
            color: status === 'COMPLETED' ? colors.green[600] : colors.amber[600],
            mr: 1,
          }}
        />
      )}
      {publicUrl !== null && (
        <Link
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: '0.8rem' }}
        >
          Open form
        </Link>
      )}
    </Box>
  );
};

export const StepInteractionFeed: React.FC<StepInteractionFeedProps> = ({
  items,
  stepTitle,
  compact = false,
}) => {
  if (items.length === 0) {
    return (
      <Box
        sx={{
          p: compact ? 2 : 4,
          textAlign: 'center',
          color: colors.textSecondary,
        }}
      >
        <Typography variant="body2">
          No activity yet for "{stepTitle}". Use the input below to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: compact ? 1 : 2,
      }}
    >
      {items.map((item) => (
        <FeedItemBubble key={item.id} item={item} />
      ))}
    </Box>
  );
};

export default StepInteractionFeed;
